import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { fetchWithAuth } from "@/lib/fetchWithAuth";
import { getSession, onSessionChange, setSession, type SessionUser } from "@/lib/sessionStore";
import { queryClient } from "@/lib/queryClient";
import { clearApiCache } from "@/lib/api-helpers";
import { clearHubCache } from "@/lib/hooks/use-current-hub";
import { primeCachedUser } from "@/lib/hooks/use-user";
import { useLocation } from "wouter";

interface UserSummary {
  id: string;
  email?: string;
  name?: string;
  username?: string;
  displayName?: string;
  account_role?: string;
  subscription?: string;
  roles?: string[];
  permissions?: string[];
}

interface ImpersonationStatus {
  active: boolean;
  authenticatedUser?: UserSummary;
  effectiveUser?: UserSummary;
  impersonating?: UserSummary & { startedAt: string };
}

interface ImpersonationContextValue {
  status: ImpersonationStatus;
  isLoading: boolean;
  refresh: () => Promise<void>;
  exit: () => Promise<void>;
}

const inactiveStatus: ImpersonationStatus = { active: false };
const ImpersonationContext = createContext<ImpersonationContextValue | null>(null);

function toSessionUser(user: UserSummary, previous: SessionUser | null): SessionUser {
  return {
    ...(previous || {}),
    id: user.id,
    email: user.email,
    name: user.name || user.displayName,
    account_role: user.account_role || "user",
    subscription: user.subscription || "free",
    roles: user.roles || [],
    permissions: user.permissions || [],
  } as SessionUser;
}

export function ImpersonationProvider({ children }: { children: React.ReactNode }) {
  const [, navigate] = useLocation();
  const [status, setStatus] = useState<ImpersonationStatus>(inactiveStatus);
  const [isLoading, setIsLoading] = useState(false);
  const lastEffectiveUserId = useRef<string | null>(null);

  const applyEffectiveUser = useCallback((nextStatus: ImpersonationStatus) => {
    const effectiveUser = nextStatus.effectiveUser;
    if (!effectiveUser?.id) return;

    const session = getSession();
    const changed = session.user?.id !== effectiveUser.id;
    const nextUser = toSessionUser(effectiveUser, session.user);

    if (changed) {
      clearApiCache();
      clearHubCache();
      queryClient.clear();
      primeCachedUser({ ...nextUser, isAuthenticated: true });
      setSession({ user: nextUser });
      queryClient.setQueryData(["user"], { ...nextUser, isAuthenticated: true });
    }

    lastEffectiveUserId.current = effectiveUser.id;
    setStatus(nextStatus);
  }, []);

  const refresh = useCallback(async () => {
    const session = getSession();
    if (!session.initialized || session.mode === "none" || !session.accessToken) {
      setStatus(inactiveStatus);
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetchWithAuth("/api/session/impersonation");
      if (!response.ok) return;
      const nextStatus = (await response.json()) as ImpersonationStatus;
      applyEffectiveUser(nextStatus);
    } catch (error) {
      console.warn("[Impersonation] Context refresh failed:", error);
    } finally {
      setIsLoading(false);
    }
  }, [applyEffectiveUser]);

  const exit = useCallback(async () => {
    const response = await fetchWithAuth("/api/admin/stop-impersonation", { method: "POST" });
    if (!response.ok) {
      throw new Error("Failed to exit impersonation");
    }
    await refresh();
    navigate("/admin");
  }, [navigate, refresh]);

  useEffect(() => {
    void refresh();
    const unsubscribe = onSessionChange(() => void refresh());
    const interval = window.setInterval(() => void refresh(), 5000);
    return () => {
      unsubscribe();
      window.clearInterval(interval);
    };
  }, [refresh]);

  const activeUser = status.impersonating;

  return (
    <ImpersonationContext.Provider value={{ status, isLoading, refresh, exit }}>
      {status.active && activeUser && (
        <div className="fixed top-0 left-0 right-0 z-[100] bg-amber-100 border-b border-amber-400 px-4 py-2 text-amber-950 shadow-sm">
          <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 text-sm">
            <span className="font-semibold">
              ⚠ IMPERSONATING: {activeUser.displayName || activeUser.name || activeUser.email || activeUser.id}
            </span>
            <button
              type="button"
              onClick={() => void exit()}
              disabled={isLoading}
              className="rounded border border-amber-500 px-3 py-1 font-medium hover:bg-amber-200 disabled:opacity-60"
            >
              {isLoading ? "Exiting…" : "Exit Impersonation"}
            </button>
          </div>
        </div>
      )}
      <div className={status.active ? "pt-10" : undefined}>{children}</div>
    </ImpersonationContext.Provider>
  );
}

export function useImpersonation(): ImpersonationContextValue {
  const value = useContext(ImpersonationContext);
  if (!value) throw new Error("useImpersonation must be used inside ImpersonationProvider");
  return value;
}